import axios, { type AxiosError } from "axios";
import { toast } from "sonner";
import authProvider from "src/providers/auth-provider";
import type { ErrorResponse } from "src/types";

const handle2FAChallenge = async (
	originalError: AxiosError,
): Promise<unknown> => {
	while (true) {
		try {
			const code = await show2FAModal();

			if (!originalError.config) {
				return Promise.reject(originalError);
			}

			originalError.config.headers["X-2FA-Code"] = code;
			return await axios.request(originalError.config);
		} catch (retryError: unknown) {
			if (
				axios.isAxiosError(retryError) &&
				retryError.response?.status === 403 &&
				retryError.response?.data?.status === "invalid_code"
			) {
				toast.error("Invalid verification code", {
					description: "Please check your authenticator app and try again.",
				});
				continue;
			}
			return Promise.reject(retryError);
		}
	}
};

const apiRequest = axios.create({
	baseURL: import.meta.env.VITE_BACKEND_HOST,
	headers: {
		"Content-Type": "application/json",
		"Cache-Control": "no-cache",
		Accept: "application/json",
	},
	withCredentials: true,
});

let pendingVerification: {
	resolve: (code: string) => void;
	reject: () => void;
} | null = null;

export const show2FAModal = (): Promise<string> => {
	return new Promise((resolve, reject) => {
		pendingVerification = { resolve, reject };
		window.dispatchEvent(
			new CustomEvent("show2FAModal", {
				detail: {
					title: "Verify Your Identity",
					description: "This action requires two-factor authentication",
				},
			}),
		);
	});
};

export const resolve2FAModal = (code: string) => {
	if (pendingVerification) {
		pendingVerification.resolve(code);
		pendingVerification = null;
	}
};

export const reject2FAModal = () => {
	if (pendingVerification) {
		pendingVerification.reject();
		pendingVerification = null;
	}
};

apiRequest.interceptors.response.use(undefined, async (error: AxiosError) => {
	if (!error.response) {
		return Promise.reject(error);
	}

	if (error.response.status === 400) {
		const response: ErrorResponse = error.response.data as ErrorResponse;

		if (!response?.errors) {
			return Promise.reject(error);
		}

		const errors = response.errors.length
			? response.errors
					.map((item) => {
						return item.message;
					})
					.join(", ")
			: "";

		if (response.status === "validation_error") {
			toast.error(response.message, {
				description: !errors
					? "Validation error"
					: `Validation error: ${errors}.`,
			});
		} else {
			toast.error(response.message, {
				description: !errors
					? "Validation error"
					: `Got the following errors: ${errors}.`,
			});
		}
	} else if (error.response.status === 403) {
		const responseData = error.response.data as {
			status?: string;
			message?: string;
			error?: string;
		};

		if (
			responseData?.status === "2fa_verification_required" ||
			responseData?.error === "2fa_verification_required"
		) {
			return handle2FAChallenge(error);
		}

		try {
			const newToken = await authProvider.getCookie();

			if (error.config?.headers && newToken.length) {
				error.config.headers["x-csrf-token"] = newToken;
				return axios.request(error.config);
			}
		} catch (e) {
			console.error("Ocurred a error: ", e);
		}
	} else if (error.response.status !== 401) {
		toast.error("Something went wrong", {
			description: error.message,
		});
	}

	return Promise.reject(error);
});

export default apiRequest;
